/**
 * Registry of partners using the /generic/* sample-request flow.
 *
 * To onboard a new partner: add a config file in this directory and register it
 * in GENERIC_PARTNERS below. No component edits needed.
 *
 * Note: Medvantx is intentionally NOT registered here — it lives on the legacy
 * /medvantx/* endpoints and Pages/SampleOrderForm/* components.
 */

import type { OrderItem } from "../../api/samplesIntegrationApi";
import type { PartnerConfig, PartnerCopy } from "./types";
import caretria from "./caretria";

export type { PartnerConfig, PartnerCopy, PartnerBranding, DrugConfig } from "./types";

export const GENERIC_PARTNERS: Record<string, PartnerConfig> = {
  [caretria.id]: caretria,
};

export function getPartnerConfig(partnerId: string | undefined | null): PartnerConfig | null {
  if (!partnerId) return null;
  return GENERIC_PARTNERS[partnerId] ?? null;
}

export function isGenericPartner(partnerId: string | undefined | null): boolean {
  return getPartnerConfig(partnerId) !== null;
}

/**
 * VC / form-data drugName casing is not stable (prod Stiolto is ``stiolto``;
 * QA was ``Stiolto``). Match catalog keys case-insensitively so Quantity /
 * Product / hyphenated NDC still resolve.
 */
function resolveDrugConfig(partnerId: string, drugName: string | undefined | null) {
  const config = getPartnerConfig(partnerId);
  if (!config || !drugName) return undefined;
  const exact = config.drugs[drugName];
  if (exact) return exact;
  const needle = drugName.trim().toLowerCase();
  return Object.entries(config.drugs).find(([key]) => key.toLowerCase() === needle)?.[1];
}

/**
 * Resolve products for a (partner, drugName) pair. Falls back to whatever the
 * backend returned in decision.products if the drug isn't in the config.
 */
export function resolveDrugProducts(
  partnerId: string,
  drugName: string | undefined | null,
  fallbackProducts?: OrderItem[]
): OrderItem[] {
  const drug = resolveDrugConfig(partnerId, drugName);
  if (drug) return drug.products;
  return fallbackProducts || [];
}

/**
 * Resolve copy for a (partner, drugName) pair: defaultCopy with per-drug overrides applied.
 */
export function resolveDrugCopy(
  partnerId: string,
  drugName: string | undefined | null
): PartnerCopy | null {
  const config = getPartnerConfig(partnerId);
  if (!config) return null;
  const override = resolveDrugConfig(partnerId, drugName)?.copy;
  return { ...config.defaultCopy, ...(override || {}) };
}
